import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from 'styled-components';
import * as yup from 'yup';
import * as Sentry from '@sentry/react';

// i18n (mensajes de validación fuera del render, patrón pauseTimerSchema)
import i18n from '../../../../i18n';

// Components
import ModalContainer from '../../containers/ModalContainer';
import SearchableDropdown from '../../../inputs/SearchableDropdown';
import { InputTextArea } from '../../../inputs/InputTextArea';

// Icons
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

// Types
import { ModalProps, ChangePauseCauseFormData } from './types';

// Styles — composición centrada espejo de ChangeStateModal (chip → modal).
import {
  ContainerForm,
  ContainerText,
  Title,
  Text,
  Section,
  TextAreaWrap,
  ConsequenceNotice,
  ButtonRow,
  ActionButton,
} from './styles';

const changePauseCauseSchema: yup.ObjectSchema<ChangePauseCauseFormData> = yup
  .object()
  .shape({
    pauseCause: yup.string().required(() => i18n.t('validation:required')),
    pauseCauseDetail: yup.string().optional(),
  });

/**
 * ChangePauseCauseModal — V4 D6 (2026-07-06).
 *
 * Se abre al clicar el chip "Causa: X" de la card de un tramo CERRADO
 * (mismo gesto que chip de estado → ChangeStateModal en WorkOrder). Resuelve
 * el caso "elegí mal la causa en el modal de pausa" (spec §352-360):
 *
 * - Preselecciona la causa/detalle actuales; Desar deshabilitado sin cambios.
 * - Si el tramo tiene una Order vinculada y la causa cambia, avisa de la
 *   desvinculación (la ejecuta el backend en `updatePauseCause`).
 * - Si la causa nueva es `action`, avisa de que se encadenará la creación de
 *   una Actuació básica vinculada (el consumidor lanza la misma cadena
 *   FR-13.3 que el modal de pausa P13).
 *
 * Contrato SR-family (PauseTimerModal / NewSurveillanceSegmentModal): el
 * modal NO ejecuta la mutation — entrega el payload a `data.onConfirm`.
 */
export const ChangePauseCauseModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'modals', 'common']);
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ChangePauseCauseFormData>({
    resolver: yupResolver(changePauseCauseSchema),
    defaultValues: {
      pauseCause: data.currentCause ?? '',
      pauseCauseDetail: data.currentDetail ?? '',
    },
  });

  // V4 D7: 3 causas — mismas keys que el modal de pausa (P13). El consumidor
  // puede acotarlas por rol (`allowedCauses`, 15-oct: sala sin «Actuación»).
  const causeOptions = [
    { value: 'basic', label: t('pages:surveillance.pause.causes.basic') },
    { value: 'action', label: t('pages:surveillance.pause.causes.action') },
    {
      value: 'external-call',
      label: t('pages:surveillance.pause.causes.external-call'),
    },
  ].filter(
    (option) => !data.allowedCauses || data.allowedCauses.includes(option.value)
  );

  const selectedCause = watch('pauseCause');
  const selectedDetail = watch('pauseCauseDetail');
  const causeChanged = selectedCause !== data.currentCause;
  const detailChanged =
    (selectedDetail ?? '') !== (data.currentDetail ?? '');
  const willUnlink = causeChanged && Boolean(data.linkedOrderRoid);
  const willCreateAction = causeChanged && selectedCause === 'action';

  const handleClose = useCallback(() => {
    reset();
    onClickClose();
  }, [reset, onClickClose]);

  const onSubmit = useCallback(
    async (formData: ChangePauseCauseFormData) => {
      try {
        setIsSubmitting(true);
        await data.onConfirm({
          pauseCause: formData.pauseCause,
          pauseCauseDetail: formData.pauseCauseDetail,
        });
        // Cerrar tras confirmar (fix 2026-07-03 del PauseTimerModal: sin esto
        // el modal queda montado tapando la navegación de la cadena →action).
        handleClose();
      } catch (err) {
        // El consumidor ya toastea el error (useSurveillanceRoutes); el modal
        // queda abierto para reintentar o cancelar.
        Sentry.captureException(err, {
          tags: {
            feature: 'surveillance-route',
            component: 'ChangePauseCauseModal',
          },
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [data, handleClose],
  );

  return (
    <ModalContainer
      // width 60 (el de EditTeamModal, el form-modal ancho canónico): con 50
      // el textarea de detalle quedaba estrecho (feedback usuario 2026-07-06).
      // El título va DENTRO (centrado, con icono) como ChangeStateModal, no
      // en la cabecera → aquí no se pasa `title`.
      width={60}
      onClickClose={handleClose}
      fill={theme.colors.black}
    >
      <ContainerForm onSubmit={handleSubmit(onSubmit)}>
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">
            {t('pages:surveillance.pauseModal.title')}
          </Title>
          <Text size="M" colorText="greyDark">
            {t('modals:changePauseCause.description')}
          </Text>
        </ContainerText>

        {/* Cause — label centrado + dropdown, patrón ChangeStateModal */}
        <Section>
          <Text size="S">{t('pages:surveillance.pause.cause')}</Text>
          <Controller
            name="pauseCause"
            control={control}
            render={({ field }) => (
              <SearchableDropdown
                inputSize="L"
                typeDropDown="Default"
                placeholder={t('pages:surveillance.pauseModal.selectCause')}
                options={causeOptions}
                onChangeSelected={(option) =>
                  field.onChange(option?.value || '')
                }
                selectedOption={field.value}
                error={errors.pauseCause?.message}
              />
            )}
          />
        </Section>

        {/* Detail (optional) */}
        <Section>
          <Text size="S">{t('pages:surveillance.pauseModal.detail')}</Text>
          <TextAreaWrap>
            <Controller
              name="pauseCauseDetail"
              control={control}
              render={({ field }) => (
                <InputTextArea
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder={t(
                    'pages:surveillance.pauseModal.detailPlaceholder',
                  )}
                  disabled={false}
                />
              )}
            />
          </TextAreaWrap>
        </Section>

        {/* Consecuencias del cambio (D6): desvinculación y/o cadena →action */}
        {willUnlink && (
          <ConsequenceNotice>
            {t('modals:changePauseCause.unlinkWarning', {
              roid: data.linkedOrderRoid,
            })}
          </ConsequenceNotice>
        )}
        {willCreateAction && (
          <ConsequenceNotice>
            {t('modals:changePauseCause.actionInfo')}
          </ConsequenceNotice>
        )}

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          />
          <ActionButton
            type="submit"
            text={t('common:buttons.save')}
            disabled={isSubmitting || (!causeChanged && !detailChanged)}
          />
        </ButtonRow>
      </ContainerForm>
    </ModalContainer>
  );
};

export default ChangePauseCauseModal;
